import { Controller } from "@hotwired/stimulus";
import { patch } from "@rails/request.js";

// Connects to data-controller="import-template-form"
export default class extends Controller {
  static targets = [
    "addColumnForm", 
    "addColumnModal",
    "addColumnNameInput",
    "columnConfigErrors",
    "columnConfigurationFormCard", 
    "columnCountInput", 
    "columnPreviewCard", 
    "columnPreviewTable", 
    "columnTableRow", 
    "fieldTypeSelect",
    "form", 
    "formStepper", 
    "importTypeOption",
    // Targets related to datetime format modals
    "customFormatGroup",
    "customFormatInput",
    "dateTimeFormatForm",
    "dateTimeFormatSelect",
    "cumulativeOptions",
    "interspersedWarning"
  ];

  static values = {
    importTemplateId: String,
  }

  addColumnModalTargetConnected(modal) {
    this._clearAddColumnFieldsHandler = () => this.clearAddColumnFields();
    $(modal).on("hidden.bs.modal", this._clearAddColumnFieldsHandler);
  }

  addColumnModalTargetDisconnected(modal) {
    $(modal).off("hidden.bs.modal", this._clearAddColumnFieldsHandler);
  }

  columnTableRowTargetConnected(row) {
    this.formTarget.dispatchEvent(new CustomEvent("dirtyForm.rescan"));
    this.updateRowNumbers();
  }

  columnTableRowTargetDisconnected(row) {
    this.updateRowNumbers();
  }

  updateRowNumbers() {
    this.columnTableRowTargets.forEach((row, index) => {
      row.dataset.columnRowId = index;
      row.id = `column_table_row_${index}`;
      var inputs = row.querySelectorAll(`input`);
      inputs.forEach(input => {
        const nameParts = input.name.split("[");
        nameParts[0] = `import_field_${index}`;
        input.name = nameParts.join("[");
      });

      const dateTimeFormatCell = row.querySelector('.date-time-format-cell');
      dateTimeFormatCell?.setAttribute("id", `import_field_${index}_date_time`);

      const dateTimeEditLink = dateTimeFormatCell?.querySelector(`a[href*="field_index="]`);
      if (dateTimeEditLink) {
        const originalHref = dateTimeEditLink.getAttribute("href");
        if (originalHref) {
          const updatedUrl = new URL(originalHref, window.location.origin);
          updatedUrl.searchParams.set("field_index", index);

          const isAbsoluteUrl = /^[a-z][a-z\d+\-.]*:/i.test(originalHref.replace(/-/g, ''));
          dateTimeEditLink.setAttribute(
            "href",
            isAbsoluteUrl ? updatedUrl.toString() : `${updatedUrl.pathname}${updatedUrl.search}${updatedUrl.hash}`
          );
        }
      }

      const hiddenRowNumberInput = row.querySelector(`input[type="hidden"][name$="[row_number]"]`);
      if (hiddenRowNumberInput) {
        hiddenRowNumberInput.value = index;
      }
    });
  }

  selectImportType(event) {
    const selectedOption = event.currentTarget;
    const importType = selectedOption.dataset.importTypeOptionValue;

    // Update the radio button selection
    const radioButton = selectedOption.querySelector(`input[type="radio"][value="${importType}"]`);
    if (radioButton) {
      radioButton.checked = true;
    }

    // Add active class to the selected card and remove from others
    this.importTypeOptionTargets.forEach(option => {
      option.classList.toggle("active", option === selectedOption);
    });
  }

  submitTemplateInfoForm() {
    const hasSelectedOption = this.formTarget.querySelector(`input[type="radio"]:checked`)?.value;
    
    if (!hasSelectedOption) {
      alert("Please select an import type before continuing.");
      return;
    }
    this.formTarget.requestSubmit();
  }

  loadColumnConfigurationPreview(e) {
    e.preventDefault();
    this.updatePreviewData();
    this.columnConfigurationFormCardTarget.classList.add("d-none");
    this.columnPreviewCardTarget.classList.remove("d-none");
    this.setActiveStep(3);
  }

  async updatePreviewData(silent = false) {
    if (this.formTarget.dataset.previewStale != "true") { return; }
    // add loader to preview card while waiting for response from server
    if (!silent) {
      $(this.columnPreviewTableTarget).LoadingOverlay("show");
    }
    // then call to controller to turbostream replace the column configuration preview with the updated version
    // gather the form data to send with the request
    const formData = new FormData(this.formTarget);

    try {
      const response = await patch(`/import_templates/${this.importTemplateIdValue}/update_column_preview?silent=${silent}`, {
        body: formData,
        responseKind: "turbo-stream"
      });

      if (!response.ok) {
        throw new Error(`Preview update failed with status ${response.status}`);
      }

      this.formTarget.dataset.previewStale = "false";
    } catch (error) {
      console.error("Failed to update preview data", error);
      alert("Unable to update the preview right now. Please try again.");
    } finally {
      if (!silent) {
        $(this.columnPreviewTableTarget).LoadingOverlay("hide");
      }
    }
  }

  loadColumnConfigurationForm() {
    this.columnConfigurationFormCardTarget.classList.remove("d-none");
    this.columnPreviewCardTarget.classList.add("d-none");
    this.setActiveStep(2);
  }
  
  setActiveStep(stepNumber) {
    this.formStepperTargets.forEach(stepper => {
      const step = parseInt(stepper.dataset.stepNumberValue);
      if (step === stepNumber) {
        stepper.classList.remove("d-none");
      } else {
        stepper.classList.add("d-none");
      }
    });
  }

  submitAddColumnForm() {
    this.columnCountInputTarget.value = parseInt(this.columnTableRowTargets.length);
    this.addColumnFormTarget.requestSubmit();
    $(this.addColumnModalTarget).modal('hide');
  }

  clearAddColumnFields() {
    this.addColumnNameInputTarget.value = "";
    this.addColumnNameInputTarget.dispatchEvent(new Event("input"));
    this.fieldTypeSelectTarget.value = "";
  }
      
  fillDefaultName(event) {
    this.addColumnNameInputTarget.value = event.currentTarget.selectedOptions[0].text;
    this.addColumnNameInputTarget.dispatchEvent(new Event("input"));
  }

  saveColumnConfigurationForm() {
    this.formTarget.requestSubmit();
    this.updatePreviewData(true);
  }

  deleteColumn(event) {
    const columnRow = event.currentTarget.closest(
      '[data-import-template-form-target="columnTableRow"]'
    );
    if (columnRow) {
      columnRow.remove();
    }
  }

  columnConfigErrorsTargetConnected(element) {
    this.formTarget.dispatchEvent(new CustomEvent("dirtyForm.markDirty"));
    this.loadColumnConfigurationForm();
  }

  submitDateTimeFormatForm() {
    // if select value is custom, don't submit form with an empty custom format - instead show error message
    const formatSelect = this.dateTimeFormatSelectTarget;
    const customFormatInput = this.dateTimeFormatFormTarget.querySelector("input[name='custom_format']");
    if (formatSelect.value === "custom" && customFormatInput.value.trim() === "") {
      customFormatInput.classList.add("input-border-danger");
      return;
    } else {
      this.dateTimeFormatFormTarget.requestSubmit();
    }
  }

  toggleCumulativeOptions(event) {
    this.cumulativeOptionsTarget.classList.toggle("d-none", !event.currentTarget.checked);
  }

  toggleInterspersedWarning(event) {
    this.interspersedWarningTarget.classList.toggle("d-none", event.currentTarget.value !== "interspersed");
  }

  toggleCustomFormatField(e) {
    const selectedValue = e.currentTarget.value;
    const showCustom = selectedValue === "custom";
    this.customFormatGroupTarget.classList.toggle("d-none", !showCustom);
    if (showCustom) {
      this.customFormatInputTarget.setAttribute("required", "required");
    } else {
      this.customFormatInputTarget.classList.remove("input-border-danger");
      this.customFormatInputTarget.removeAttribute("required");
    }
  }
}
